import React from 'react';
import { motion } from 'framer-motion';
import { FloatingParticles } from './FloatingParticles';
import { IPhoneMockup } from './IPhoneMockup';
import { HomeScreen, NotesMarketplaceScreen, TutorBookingScreen, TaskManagerScreen, LiveClassesScreen, NajahAIScreen, ChatsScreen } from './AppScreens';
const SCREENS = [{
  id: 1,
  component: <HomeScreen data-id="element-183" />,
  title: 'Home Dashboard',
  description: 'Central hub for all your learning needs. Access marketplace, tutors, tasks, and live classes from one beautiful interface.',
  number: '01'
}, {
  id: 2,
  component: <NotesMarketplaceScreen data-id="element-184" />,
  title: 'Notes Marketplace',
  description: 'Buy and sell high-quality study notes. Filter by rating, subject, and price to find exactly what you need.',
  number: '02'
}, {
  id: 3,
  component: <TutorBookingScreen data-id="element-185" />,
  title: 'Tutor Booking',
  description: 'Find expert tutors for any subject. Browse profiles, check rates, and book sessions instantly.',
  number: '03'
}, {
  id: 4,
  component: <TaskManagerScreen data-id="element-186" />,
  title: 'Smart Task Manager',
  description: 'Stay organized with intelligent task tracking. Includes Emergency Focus Mode to block distractions when deadlines approach.',
  number: '04'
}, {
  id: 5,
  component: <LiveClassesScreen data-id="element-187" />,
  title: 'Live Classes',
  description: 'Subscribe to premium live courses. Interactive learning experiences with expert instructors.',
  number: '05'
}, {
  id: 6,
  component: <NajahAIScreen data-id="element-188" />,
  title: 'Najah AI',
  description: 'Your personal AI study companion. Ask questions, get explanations, and receive personalized study tips 24/7.',
  number: '06'
}, {
  id: 7,
  component: <ChatsScreen data-id="element-189" />,
  title: 'Chats',
  description: 'Connect with tutors and peers. Discuss notes, schedule sessions, and collaborate on assignments.',
  number: '07'
}];
export function IPhoneShowcase() {
  return <div className="relative w-full min-h-screen bg-[#FDF8F3] overflow-x-hidden" data-id="element-190">
      {/* Background */}
      <div className="fixed inset-0 z-0 opacity-50 pointer-events-none" data-id="element-191">
        <FloatingParticles data-id="element-192" />
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FDF8F3]/80 backdrop-blur-md border-b border-[#F5F0E6] py-4 px-6 md:px-12 flex justify-between items-center" data-id="element-193">
        <h1 className="text-xl md:text-2xl font-bold text-[#5D4E37] tracking-wide" data-id="element-194">
          INJAZ NAJAH
        </h1>
        <div className="text-sm font-medium text-[#8B6914] hidden md:block" data-id="element-195">
          App Showcase
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-24 pb-24 px-4 md:px-8 max-w-7xl mx-auto" data-id="element-196">
        <div className="flex flex-col gap-32 md:gap-48" data-id="element-197">
          {SCREENS.map((screen, index) => <Section key={screen.id} screen={screen} index={index} data-id="element-198" />)}
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-12 text-center text-[#8B6914] text-sm opacity-60" data-id="element-199">
        <p data-id="element-200">© 2026 INJAZ NAJAH. All rights reserved.</p>
      </footer>
    </div>;
}
function Section({
  screen,
  index
}: {
  screen: (typeof SCREENS)[0];
  index: number;
}) {
  const isEven = index % 2 === 0;
  return <motion.div initial={{
    opacity: 0,
    y: 100
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-100px'
  }} transition={{
    duration: 0.8,
    ease: 'easeOut'
  }} className={`flex flex-col md:flex-row items-center gap-12 md:gap-24 ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-201">
      {/* Phone Mockup */}
      <div className="flex-1 flex justify-center w-full" data-id="element-202">
        <motion.div whileHover={{
        scale: 1.02,
        y: -10
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 20
      }} className="relative" data-id="element-203">
          <div className="absolute inset-0 bg-[#C9A962] opacity-20 blur-[100px] rounded-full transform scale-75" data-id="element-204"></div>
          <IPhoneMockup scale={0.9} className="shadow-2xl shadow-[#5D4E37]/20" data-id="element-205">
            {screen.component}
          </IPhoneMockup>
        </motion.div>
      </div>

      {/* Text Content */}
      <div className="flex-1 max-w-lg text-center md:text-left" data-id="element-206">
        <div className="flex items-center gap-4 mb-4 justify-center md:justify-start" data-id="element-207">
          <span className="text-6xl font-bold text-[#F5E6C8] font-serif" data-id="element-208">
            {screen.number}
          </span>
          <div className="h-px bg-[#C9A962] w-12" data-id="element-209"></div>
        </div>

        <h2 className="text-3xl md:text-4xl font-bold text-[#5D4E37] mb-6" data-id="element-210">
          {screen.title}
        </h2>
        <p className="text-lg text-[#8B6914] leading-relaxed mb-8" data-id="element-211">
          {screen.description}
        </p>

        <div className="flex gap-3 justify-center md:justify-start" data-id="element-212">
          <div className="w-3 h-3 rounded-full bg-[#C9A962]" data-id="element-213"></div>
          <div className="w-3 h-3 rounded-full bg-[#F5E6C8]" data-id="element-214"></div>
          <div className="w-3 h-3 rounded-full bg-[#F5E6C8]" data-id="element-215"></div>
        </div>
      </div>
    </motion.div>;
}